import { useEffect, useMemo, useState } from "react";
import ModuleHeader from "../../moduleHeader";
import { getEnergyStones } from "../../../services/energyStonesService";
import { getCurrentPackageUserModule } from "../../../services/api";

export default function EnergyStones({ id, moduleId, isOwner }) {
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // Paket ve kullanıcı bilgilerini çek (moduleId sabit 14 - EnergyStones)
  useEffect(() => {
    if (!id) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(id, 14);
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageData();
  }, [id]);

  // Energy Stones verisini çek
  useEffect(() => {
    if (!loading && packageData) {
      let isMounted = true;
      (async () => {
        try {
          setDataLoading(true);
          const response = await getEnergyStones();
          if (!isMounted) return;
          setData(response?.data || null);
          setError("");
        } catch (e) {
          if (!isMounted) return;
          setError("Bir şeyler ters gitti. Lütfen daha sonra tekrar deneyin.");
        } finally {
          if (isMounted) setDataLoading(false);
        }
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [loading, packageData]);

  const zodiacName = data?.zodiac_sign?.name;
  const zodiacSymbol = data?.zodiac_sign?.symbol;
  const stones = useMemo(() => data?.stones ?? [], [data]);

  // Loading durumunda loading ekranı göster
  if (loading || !packageData || dataLoading || !data) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="w-full p-6 text-center text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
              title={packageData.package?.name}
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        <div className="min-h-screen bg-white w-full px-4 pb-10">
          <div className="flex flex-col items-center gap-3 pt-4 pb-6">
            <div className="h-14 w-14 rounded-full border border-indigo-50 shadow-inner flex items-center justify-center">
              <span className="text-xl">{zodiacSymbol}</span>
            </div>
            <div className="text-center">
              <h2 className="text-base font-semibold">
                <span className="font-bold">{zodiacName}</span> burcunuzun
                enerjisini
              </h2>
              <h3 className="text-base font-semibold -mt-1">gösteren taşlar</h3>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {stones.map((stone, idx) => (
              <div
                key={`${stone.name}-${idx}`}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex gap-4 items-start"
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-xl bg-indigo-50 flex items-center justify-center overflow-hidden shrink-0">
                        {stone.image_url ? (
                          <img
                            src={stone.image_url}
                            alt={stone.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="text-indigo-400 font-semibold">
                            {stone.name?.charAt(0) || ""}
                          </span>
                        )}
                      </div>
                      <p className="text-[#434343] text-start font-semibold">
                        {stone.name} Taşı
                      </p>{" "}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const productName = encodeURIComponent(stone.name || '');
                        const userId = id || packageData?.user?.id || '';
                        const url = `https://jodi.ac/collection?category=charm&product=${productName}&user_id=${userId}`;
                        window.open(url, '_blank');
                      }}
                      className="shrink-0 max-w-28 text-xs font-semibold bg-[#EFF2F5] text-[#3F414E] px-3 py-1.5 rounded-2xl hover:bg-gray-200 transition"
                    >
                      Charm olarak satın al
                    </button>
                  </div>
                  <p className="text-sm text-start text-black mt-2 leading-5">
                    {stone.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="h-20"></div>
        </div>
      </div>
    </div>
  );
}

